import { Dialog, DialogContent, DialogTitle } from '@heidi/design-system/dialog';
import { useTranslations } from '@heidi/runtime/i18n';
import { cn, useCanShowCanvasPanel } from '@heidi/utils';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { artefactPreviewAtom } from '../user-documents/atoms';

const DocumentPreviewContent = dynamic(
  () =>
    import('./DocumentPreviewContent').then((mod) => ({
      default: mod.DocumentPreviewContent,
    })),
  { ssr: false }
);

/**
 * ArtefactPreviewModal - Modal for previewing generated artefacts.
 *
 * Unlike DocumentPreviewModal (which fetches URL from a document ID),
 * this modal receives the URL directly — used for inline preview of
 * chat-generated artefacts like PDFs and patient handouts.
 *
 * Uses DocumentPreviewContent for the shared header/content/footer UI.
 *
 * On fullscreen chat routes (desktop), the CanvasPanel handles preview instead
 * of this modal — so this component returns null in that context.
 *
 * `pathname` is the mount's route rather than a `useRouter()` read: a package
 * `/ui` zone may not import `next/router`, and the branch below is unchanged.
 */
export interface ArtefactPreviewModalProps {
  /** The mounting route's `router.pathname`. */
  pathname: string;
}

export function ArtefactPreviewModal({ pathname }: ArtefactPreviewModalProps) {
  const { t } = useTranslations();
  const canShowCanvas = useCanShowCanvasPanel();
  const [previewState, setPreviewState] = useAtom(artefactPreviewAtom);
  const { isOpen, url, filename, title, password } = previewState;

  const handleClose = useCallback(() => {
    setPreviewState({
      isOpen: false,
      url: null,
      filename: null,
      title: null,
      password: null,
    });
  }, [setPreviewState]);

  const handleDownload = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [url]);

  // Wipe the patient's DOB-derived PDF password from the artefact preview
  // atom the moment PDF.js has finished ingesting it. The atom is global
  // Jotai state inspectable by any component (and devtools), so we limit
  // the lifetime of the PHI to the actual document load instead of leaving
  // it in shared memory until the modal is closed.
  const handlePasswordConsumed = useCallback(() => {
    setPreviewState((prev) => (prev.password ? { ...prev, password: null } : prev));
  }, [setPreviewState]);

  // On fullscreen chat routes (desktop), CanvasPanel handles preview instead
  const isFullScreenEvidence = pathname.includes('/chat');
  if (isFullScreenEvidence && canShowCanvas) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={cn(
          'flex h-[90vh] max-h-[90vh] w-[90vw] max-w-6xl flex-col gap-0 overflow-hidden p-0',
          'rounded-2xl bg-surface-accent sm:rounded-2xl'
        )}
        hideCloseIcon
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{title || t('chat.preview')}</DialogTitle>
        {isOpen && (
          <DocumentPreviewContent
            title={title || t('chat.preview')}
            url={url}
            filename={filename}
            password={password ?? null}
            onClose={handleClose}
            onDownload={handleDownload}
            onPasswordConsumed={handlePasswordConsumed}
            variant="modal"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
